import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Stack,
  CircularProgress
} from '@mui/material';
import { CloseOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import axios from 'utils/axios';

const UserDialog = ({ open, onClose, userId, viewMode, isProfile, onSaveSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({
    id: '',
    username: '',
    name: '',
    email: '',
    phone_number: '',
    company: '',
    status: 'Active',
    role_id: '',
    language_preference: 'English',
    password: '',
    allow_notifications: false,
    terms_accepted: false,
    created_by: '',
    modified_by: '',
    created_at: '',
    updated_at: '',
    last_login: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  const fetchRoles = async () => {
    try {
      const res = await axios.get('/roles');
      setRoles(res.data);
    } catch (err) {
      console.error('Failed to fetch roles', err);
    }
  };

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      let res;
      if (isProfile) {
        res = await axios.get('/user');
      } else if (userId) {
        res = await axios.get(`/users/${userId}`);
      } else {
        return;
      }
      const u = res.data;
      setFormData({
        id: u.id || '',
        username: u.username || '',
        name: u.name || '',
        email: u.email || '',
        phone_number: u.phone_number || '',
        company: u.company || '',
        status: u.status || 'Active',
        role_id: u.role_id || '',
        language_preference: u.language_preference || 'English',
        password: '',
        allow_notifications: !!u.allow_notifications,
        terms_accepted: !!u.terms_accepted,
        created_by: u.created_by || '',
        modified_by: u.modified_by || '',
        created_at: u.created_at || '',
        updated_at: u.updated_at || '',
        last_login: u.last_login || ''
      });
    } catch (err) {
      console.error('Failed to fetch user details', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setValidationErrors({});
      fetchRoles();
      if (userId || isProfile) {
        fetchUserDetails();
      } else {
        setFormData({
          id: '',
          username: '',
          name: '',
          email: '',
          phone_number: '',
          company: '',
          status: 'Active',
          role_id: '',
          language_preference: 'English',
          password: '',
          allow_notifications: false,
          terms_accepted: false,
          created_by: '',
          modified_by: '',
          created_at: '',
          updated_at: '',
          last_login: ''
        });
      }
    }
  }, [open, userId, isProfile]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.username) errors.username = 'Username is required';
    if (!formData.name) errors.name = 'Full Name is required';
    if (!formData.email) errors.email = 'Email is required';
    if (!formData.company) errors.company = 'Company is required';
    if (!formData.status) errors.status = 'Status is required';
    if (!formData.id && !formData.password) {
      errors.password = 'Password is required for new users';
    }
    if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (viewMode) return;
    if (!validateForm()) return;

    try {
      let res;
      if (formData.id) {
        // If profile editing, we send a subset to ensure status/role/company are protected if needed, 
        // but the Laravel backend handles authorization/validation safely.
        res = await axios.put(`/users/${formData.id}`, formData);
      } else {
        res = await axios.post('/users', formData);
      }
      if (onSaveSuccess) {
        onSaveSuccess(res.data);
      }
      onClose();
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data && err.response.data.errors) {
        setValidationErrors(err.response.data.errors);
      } else {
        alert(err.response?.data?.message || 'Error saving user');
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0', pb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {viewMode ? 'View User Details' : (formData.id ? 'Edit User' : 'Create New User')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{
        p: 3,
        mt: 0,
        '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'black', color: 'black' },
        '& .MuiFormLabel-root.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' },
        '& .MuiFormControlLabel-label.Mui-disabled': { color: 'black' }
      }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Row 1: Username, Full Name, Email, Phone Number */}
            <Grid size={3}>
              <TextField
                name="username"
                label="Username *"
                fullWidth
                value={formData.username}
                onChange={handleChange}
                error={!!validationErrors.username}
                helperText={validationErrors.username}
                placeholder="e.g. jdoe"
                disabled={viewMode}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                name="name"
                label="Full Name *"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                placeholder="e.g. John Doe"
                disabled={viewMode}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                name="email"
                label="Email *"
                type="email"
                fullWidth
                value={formData.email}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                placeholder="e.g. john.doe@company.com"
                disabled={viewMode}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                name="phone_number"
                label="Phone Number"
                fullWidth
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="e.g. +65 9123 4567"
                disabled={viewMode}
              />
            </Grid>

            {/* Row 2: Company, Status, Roles, Language Preference, Password */}
            <Grid size={2}>
              <TextField
                name="company"
                label="Company *"
                fullWidth
                value={formData.company}
                onChange={handleChange}
                error={!!validationErrors.company}
                helperText={validationErrors.company}
                disabled={viewMode || isProfile}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                name="status"
                label="Status *"
                fullWidth
                select={!viewMode && !isProfile}
                SelectProps={{ native: true }}
                value={formData.status}
                onChange={handleChange}
                error={!!validationErrors.status}
                helperText={validationErrors.status}
                InputLabelProps={{ shrink: true }}
                disabled={viewMode || isProfile}
              >
                {viewMode || isProfile ? (
                  formData.status
                ) : (
                  <>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </>
                )}
              </TextField>
            </Grid>
            <Grid size={2}>
              <TextField
                name="role_id"
                label="Roles"
                fullWidth
                select={!viewMode && !isProfile}
                SelectProps={{ native: true }}
                value={formData.role_id}
                onChange={handleChange}
                error={!!validationErrors.role_id}
                helperText={validationErrors.role_id}
                InputLabelProps={{ shrink: true }}
                disabled={viewMode || isProfile}
              >
                {viewMode || isProfile ? (
                  roles.find((r) => String(r.id) === String(formData.role_id))?.name || 'N/A'
                ) : (
                  roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid size={2.5}>
              <TextField
                name="language_preference"
                label="Language Preference"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={formData.language_preference}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                disabled={viewMode}
              >
                <option value="">Optional...</option>
                <option value="English">English</option>
                <option value="Chinese">Chinese</option>
                <option value="Malay">Malay</option>
              </TextField>
            </Grid>
            <Grid size={4}>
              <TextField
                name="password"
                label={formData.id ? 'Password (leave blank to keep unchanged)' : 'Password *'}
                type="password"
                fullWidth
                value={formData.password}
                onChange={handleChange}
                error={!!validationErrors.password}
                helperText={validationErrors.password}
                placeholder="e.g. SecretPassword123"
                disabled={viewMode}
              />
            </Grid>

            {/* Row 3: Checkboxes */}
            <Grid size={6} sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="allow_notifications"
                    checked={formData.allow_notifications}
                    onChange={handleChange}
                    color="primary"
                    disabled={viewMode}
                  />
                }
                label="Allow Notification"
              />
            </Grid>
            <Grid size={6} sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="terms_accepted"
                    checked={formData.terms_accepted}
                    onChange={handleChange}
                    color="primary"
                    disabled={viewMode || isProfile}
                  />
                }
                label="Terms Accepted"
              />
            </Grid>

            {/* Audit Info Section */}
            {viewMode && (
              <Grid size={12}>
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Audit Info
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <TextField
                        label="Created By"
                        value={formData.created_by || ''}
                        fullWidth
                        disabled
                        size="small"
                        InputProps={{ style: { color: 'black' } }}
                      />
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        label="Date Created"
                        value={formData.created_at ? format(new Date(formData.created_at), 'yyyy-MM-dd HH:mm') : ''}
                        fullWidth
                        disabled
                        size="small"
                        InputProps={{ style: { color: 'black' } }}
                      />
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        label="Last Login"
                        value={formData.last_login ? format(new Date(formData.last_login), 'yyyy-MM-dd HH:mm') : 'Never'}
                        fullWidth
                        disabled
                        size="small"
                        InputProps={{ style: { color: 'black' } }}
                      />
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        label="Modified By"
                        value={formData.modified_by || ''}
                        fullWidth
                        disabled
                        size="small"
                        InputProps={{ style: { color: 'black' } }}
                      />
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        label="Date Modified"
                        value={formData.updated_at ? format(new Date(formData.updated_at), 'yyyy-MM-dd HH:mm') : ''}
                        fullWidth
                        disabled
                        size="small"
                        InputProps={{ style: { color: 'black' } }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
        <Button onClick={onClose} color="inherit" variant="outlined" sx={{ mr: 1 }}>
          {viewMode ? 'Close' : 'Cancel'}
        </Button>
        {!viewMode && (
          <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ px: 3 }}>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

UserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  viewMode: PropTypes.bool,
  isProfile: PropTypes.bool,
  onSaveSuccess: PropTypes.func
};

export default UserDialog;
